import { Form, Progress, Row, Col } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import Cookies from 'js-cookie';
import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import useMarketoFormData from '../../../hooks/useMarketoFormData';
import RadioGroup from 'components/Form/RadioGroup';
import Select from 'components/Form/Select';
import Button from 'components/Button';
import { WebsiteImage } from 'components/TitleDescription';
import SliderField from 'components/Form/SliderField';
import TextField from 'components/Form/TextField';
import RevealContent from 'components/RevealContent';
import { mapFormFieldValues } from 'utils/marketo';
import isNil from 'utils/isNil';
import { clearCookies, getSetCookies } from 'utils/cookieUtility';
import snackbar from 'utils/snackbar/snackbar';
import { Link } from 'gatsby';
import { init, saveForm } from 'services/marketoService';
import { FormContainer, FormTitle, FormStep } from './styles';
import Text from 'components/Text';
import useNavigate from 'utils/useNavigate';
import { handleCustomDQ } from './utils/redirectData';

const validFieldTypes = [
  'text',
  'textArea',
  'telephone',
  'email',
  'select',
  'radioButtons',
  'checkbox',
  'number',
];

const DynamicFormField = ({
  field,
  value,
  formErrors,
  onHandleBlur,
  onHandleChange,
  onHandleFocus,
}) => {
  const formValid = {};
  formValid[field.id] =
    formErrors[field.id] && formErrors[field.id].length > 0 ? -1 : value ? 1 : 0;

  if (
    field.dataType === 'text' ||
    field.dataType === 'telephone' ||
    field.dataType === 'email' ||
    field.dataType === 'textArea'
  ) {
    return (
      <TextField
        id={field.id}
        type={field.dataType === 'telephone' ? 'number' : 'text'}
        format={field.dataType === 'telephone' ? '(###) ###-####' : ''}
        nameType={field.label}
        placeholderText={field.hintText || field.label}
        value={value}
        formValid={formValid}
        formErrors={formErrors}
        handleBlur={onHandleBlur}
        handleChange={onHandleChange}
      />
    );
  } else if (field.dataType === 'select') {
    const placeholderText = field?.fieldMetaData?.values[0]?.label;
    const { values, multiSelect } = field?.fieldMetaData;

    return (
      <Select
        id={field.id}
        nameType={field.label}
        formValid={formValid}
        label={multiSelect && field.label}
        placeholder={!multiSelect && `Select ${field.label}`}
        options={values}
        handleChange={onHandleChange}
        value={value}
        multiple={multiSelect}
      />
    );
  } else if (field.dataType === 'radioButtons' || field.dataType === 'checkbox') {
    return (
      <RadioGroup
        id={field.id}
        nameType={field.label}
        options={
          field?.fieldMetaData?.values || [
            { value: 'Yes', label: 'Yes' },
            { value: 'No', label: 'No' },
          ]
        }
        value={value}
        formValid={formValid}
        formErrors={formErrors}
        handleChange={onHandleChange}
        handleFocus={onHandleFocus}
      />
    );
  } else if (field.dataType === 'number') {
    return (
      <SliderField
        id={field.id}
        nameType={field.label}
        min={field?.fieldMetaData?.minValue || 0}
        max={field?.fieldMetaData?.maxValue || 100}
        value={value || 0}
        formValid={formValid}
        formErrors={formErrors}
        handleChange={onHandleChange}
        logorthmic={true}
      />
    );
  }
};

const sliderSettings = {
  dots: false,
  arrows: false,
  infinite: true,
  draggable: false,
  touchMove: false,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  adaptiveHeight: true,
  accessibility: false,
  cssEase: 'cubic-bezier(0.55, 0.085, 0.68, 0.53)',
};

export const getMarketoForm = (id, forms) => (forms || useMarketoFormData()).find(x => x.id == id);
export const filterFields = (ar, types = validFieldTypes) =>
  ar?.filter(field => types.includes(field?.dataType));

const DynamicMarketoForm = ({
  dataPosition,
  width,
  height,
  backgroundColor,
  title = '',
  titleColor,
  titleImage,
  titleSize,
  titleBackgroundColor,
  buttonLabel,
  buttonType = 'primary',
  footer,
  formType,
  shadow,
  marketoFormId,
  marketoFormData,
  redirectUrl,
  onSubmit,
  onSuccess,
  formName,
  showPrivacy = true,
  termsText,
  revealProps = false,
  notificationStyles,
  notificationMessage,
  notificationSnack,
  shouldRedirect = true,
  useCustomDqFlow,
  ...otherProps
}) => {
  const [values, setValues] = useState({
    FirstName: Cookies.get('firstName') || '',
    LastName: Cookies.get('lastName') || '',
    Company: Cookies.get('company') || '',
    Email: Cookies.get('email') || '',
    Phone: Cookies.get('mobilePhone') || '',
    utm_source: Cookies.get('utm_source') || null,
    utm_medium: Cookies.get('utm_medium') || null,
    utm_campaign: Cookies.get('utm_campaign') || null,
    utm_content: Cookies.get('utm_content') || null,
    utm_term: Cookies.get('utm_term') || null,
    utm_promo: Cookies.get('utm_promo') || null,
    microsoftClickID: Cookies.get('msclkid') || null,
    fbclid: Cookies.get('fbclid') || null,
    gclid: Cookies.get('gclid') || '',
    creative: Cookies.get('creative') || '',
    keyword: Cookies.get('keyword') || '',
    matchtype: Cookies.get('matchtype') || '',
    employee: Cookies.get('employee') || null,
  });

  const marketoFormDetails = marketoFormId && getMarketoForm(marketoFormId, marketoFormData);
  let slider;

  const [formErrors, setFormErrors] = useState({});
  const [step, setStep] = useState(0);
  const [showReveal, setShowReveal] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const hasImage = !isNil(titleImage);

  const isProfiling =
    marketoFormDetails && marketoFormDetails.fields?.some(item => item.id === 'Profiling');
  const steps =
    (marketoFormDetails &&
      marketoFormDetails.fields
        ?.filter(x => x.dataType === 'fieldset')
        .map(x => ({
          ...x,
          fields: marketoFormDetails.fields?.filter(y => x.fields?.includes(y.id)),
        }))) ||
    [];

  if (marketoFormDetails && steps.length === 0) {
    steps.push({
      fields: marketoFormDetails.fields,
    });
  }

  buttonLabel = buttonLabel || (marketoFormDetails && marketoFormDetails.buttonLabel) || 'Submit';

  const evalVisibilityRule = x => {
    switch (x.operator.toLowerCase()) {
      case 'is':
        return x.values.includes(values[x.subjectField]);
      case 'is not':
        return !x.values.includes(values[x.subjectField]);
      case 'is empty':
        return !values[x.subjectField] || values[x.subjectField].length === 0;
      case 'is not empty':
        return values[x.subjectField] && values[x.subjectField].length > 0;
      case 'contains':
        return x.values.some(y => values[x.subjectField] && values[x.subjectField].includes(y));
      case 'not contains':
        return x.values.every(y => !values[x.subjectField] || !values[x.subjectField].includes(y));
      default:
        return false;
    }
  };

  const getVisibleFields = fields =>
    filterFields(fields).filter(
      field =>
        !field.visibilityRules ||
        field.visibilityRules.ruleType === 'alwaysShow' ||
        field.visibilityRules.rules.every(evalVisibilityRule)
    );

  const allFormFields = getVisibleFields(steps.flatMap(x => x.fields));

  useEffect(() => {
    const valuesObj = {};
    const formErrorsObj = {};

    steps.length &&
      getVisibleFields(steps[step].fields)?.forEach(field => {
        valuesObj[field.id] = values[field.id];

        if (!valuesObj[field.id] && field.defaultValue) {
          switch (field.dataType) {
            case 'number':
              valuesObj[field.id] = parseInt(field.defaultValue);
              break;
            case 'select':
              valuesObj[field.id] = field.defaultValue !== '[""]' ? field.defaultValue : '';
              break;
            default:
              valuesObj[field.id] = field.defaultValue;
              break;
          }
        }
        formErrorsObj[field.id] = formErrors[field.id] || '';
      });

    const tempValues = getSetCookies(valuesObj);

    setValues({ ...values, ...tempValues });

    setFormErrors({ ...formErrors, ...formErrorsObj });
  }, [step]);

  useEffect(() => {
    init(marketoFormId);
  }, []);

  const getFormPercent = () => {
    const validCount = allFormFields.reduce((cur, field) => {
      const id = field.id;
      return cur + (validateField(id, values[id]).length > 0 ? 0 : 1);
    }, 0);

    return (validCount / allFormFields.length) * 100;
  };

  const onHandleFocus = id => {};
  const onHandleBlur = (id, newValue) => {
    checkFieldValidate(id, newValue);
  };
  const onHandleChange = (id, newValue) => {
    const tmpValues = { ...values };
    tmpValues[id] = newValue;
    if (Array.isArray(tmpValues[id]) && tmpValues[id].length === 0) {
      tmpValues[id] = null;
    }
    setValues(tmpValues);
    checkFieldValidate(id, newValue);
    setShowReveal(showReveal ? true : newValue != false);
  };
  const validateField = (id, value) => {
    const field = allFormFields.find(x => x.id === id);

    if (!field.required) {
      return '';
    }

    if (value === '' || isNil(value)) {
      return `${field.label} is required`;
    }

    switch (field.dataType) {
      case 'telephone':
        return value.length !== 14 ? `${field.label} is invalid` : '';
      case 'email':
        return value.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)
          ? ''
          : `${field.label} is invalid`;
    }

    return '';
  };
  const checkFieldValidate = (id, value) => {
    const fieldValidationErrors = { ...formErrors };
    fieldValidationErrors[id] = validateField(id, value);
    setFormErrors(fieldValidationErrors);
  };
  const prevStep = () => {
    const newStep = step - 1;
    setStep(newStep);
    if (newStep === 0) {
      slider.slickGoTo(newStep, true);
    } else {
      slider.slickPrev();
    }
  };
  const nextStep = async () => {
    const newStep = step + 1;

    const fieldValidationErrors = { ...formErrors };

    const invalidCount = getVisibleFields(steps[step].fields).reduce((count, fields) => {
      fieldValidationErrors[fields.id] = validateField(fields.id, values[fields.id]);
      return count + (fieldValidationErrors[fields.id] ? 1 : 0);
    }, 0);

    setFormErrors(fieldValidationErrors);

    if (invalidCount) {
      return;
    }

    if (newStep < steps.length) {
      setStep(newStep);
      if (step === 0) {
        slider.slickGoTo(newStep, true);
      } else {
        slider.slickNext();
      }
    } else {
      await submit();
    }
  };
  const submit = async () => {
    let response;

    const data = {
      ...values,
      step: step + 1,
      marketoFormId,
      ...((values.areaOfFocus || values.typeOfBusiness) && {
        businessFocus: `${data.areaOfFocus || ''}${
          data.typeOfBusiness ? ' ' + data.typeOfBusiness : ''
        }`,
      }),
    };

    if (onSubmit) {
      onSubmit(data);
    }

    try {
      response = await saveForm(data);
    } catch (e) {}

    Cookies.set('firstName', values.FirstName);
    Cookies.set('lastName', values.LastName);
    Cookies.set('company', values.Company);
    Cookies.set('email', values.email || values.Email);
    Cookies.set('mobilePhone', values.Phone);

    Bizible &&
      Bizible.Push('User', {
        eMail: values.email || values.Email, // required
      });

    const dataLayerData = {
      event: 'form submitted',
      form_type: formType || 'Dynamic Marketo Form',
      ...(dataPosition && { 'data-position': dataPosition }),
      ...mapFormFieldValues(values),
    };

    dataLayer.push(dataLayerData);

    if (isProfiling) {
      Cookies.set('marketo_form_' + marketoFormId + '_submit', JSON.stringify(dataLayerData));
    }

    clearCookies(values);
    setIsSubmitted(true);

    if (onSuccess) {
      onSuccess(setValues);
    }

    if (notificationSnack && notificationMessage) {
      snackbar(notificationMessage);
    }
    if (useCustomDqFlow) {
      handleCustomDQ(data, marketoFormId);
      return;
    }

    if (shouldRedirect) {
      if (redirectUrl) {
        useNavigate(redirectUrl);
        return;
      }

      if (response && response.data.followUpUrl) {
        useNavigate(response.data.followUpUrl);
      }
    }
  };

  const ButtonText = () => {
    if (step + 1 < steps.length) {
      return <span>Next</span>;
    }

    return (
      <>
        {buttonLabel === 'Download' && <DownloadOutlined />}
        {buttonLabel}
      </>
    );
  };

  const TermsText = () => {
    if (termsText && termsText['en-US'] && termsText['en-US']?.nodeType) {
      return documentToReactComponents(termsText['en-US']);
    }
    if (termsText?.json && termsText?.json?.nodeType) {
      return documentToReactComponents(termsText.json);
    }
    return termsText;
  };

  return (
    <FormContainer
      className="signup-form--container"
      width={width}
      height={height}
      backgroundColor={backgroundColor}
      titleColor={titleColor}
      shadow={shadow}
      {...otherProps}
    >
      <Row>
        {step > 0 && (
          <Col flex={true} sm={2}>
            <button
              data-position="Form - CTA"
              type="button"
              onClick={prevStep}
              className="slick-prev"
            >
              Previous
            </button>
          </Col>
        )}
        {(title || hasImage) && (
          <Col flex={true}>
            <FormTitle className="signup-form--title" titleColor={titleColor} titleSize={titleSize}>
              {hasImage && (
                <WebsiteImage
                  className="signup-form--title-image"
                  src={titleImage.file.url}
                  alt={title}
                  width="100%"
                />
              )}
              {!hasImage && title}
            </FormTitle>
          </Col>
        )}
      </Row>
      <Form data-position={dataPosition || formName} className="signup-form--form">
        <Slider ref={c => (slider = c)} {...sliderSettings}>
          {steps?.map((curStep, index) => (
            <FormStep key={index} className="data-layer-form">
              {getVisibleFields(curStep.fields)?.map((field, index) => {
                return (
                  <Form.Item key={index}>
                    <DynamicFormField
                      field={field}
                      value={values[field.id]}
                      formErrors={formErrors}
                      onHandleBlur={onHandleBlur}
                      onHandleChange={onHandleChange}
                      onHandleFocus={onHandleFocus}
                    />
                  </Form.Item>
                );
              })}
              <Form.Item data-position="Form - Submit CTA">
                <Button
                  className="signup-form--submit-button"
                  type={buttonType}
                  width="100%"
                  onClick={nextStep}
                  disabled={isSubmitted}
                >
                  <ButtonText />
                </Button>
              </Form.Item>
              {revealProps && (
                <RevealContent
                  showContent={showReveal}
                  content={revealProps.content}
                  styles={revealProps.styles}
                />
              )}
            </FormStep>
          ))}
        </Slider>
        {isSubmitted && notificationMessage && !notificationSnack && (
          <Text
            type="p"
            size="normal"
            className={notificationStyles || 'section-hero--notification'}
            color="#00bb22"
          >
            {notificationMessage}
          </Text>
        )}
        {allFormFields.length > 1 && (
          <Progress
            percent={getFormPercent()}
            showInfo={false}
            strokeWidth={6}
            strokeColor="#4AB971"
          />
        )}
        {showPrivacy && (
          <div className="signup-form--footer">
            <TermsText />
          </div>
        )}
      </Form>
    </FormContainer>
  );
};

DynamicMarketoForm.defaultProps = {
  backgroundColor: '#ffffff',
  titleColor: 'var(--normal-title-color)',
  title: 'Let’s get started.',
  buttonType: 'primary',
  footer: '',
  width: '100%',
  shadow: true,
  termsText: (
    <>
      By submitting this form, I confirm that I have read and agree to the{' '}
      <Link to="/privacy-policy">Privacy Statement.</Link>
    </>
  ),
  notificationSnack: false,
};

export default DynamicMarketoForm;
